import firebase from 'firebase/app';
import { decode } from 'html-entities';
import {
  ConversationCheckAvailability,
  ConversationCheckAvailabilityProducts,
  CheckAvailabilityProduct,
  Message,
  BotAgentMessage,
  QuickRepliesMessage,
  CustomPayload,
  MessageType,
  // Feature,
} from '@tomis-tech/types';

export function hexToRgb(hex: string) {
  // Expand shorthand form (e.g. "03F") to full form (e.g. "0033FF")
  const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
  const cleanHex = hex.replace(shorthandRegex, function(m, r, g, b) {
    return r + r + g + g + b + b;
  });

  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(cleanHex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : { r: '100', g: '100', b: '100' };
}

export function numToShortMonth(month: number) {
  let monthStr = 'Jan';

  switch (month) {
    case 1:
      monthStr = 'Feb';
      break;
    case 2:
      monthStr = 'Mar';
      break;
    case 3:
      monthStr = 'Apr';
      break;
    case 4:
      monthStr = 'May';
      break;
    case 5:
      monthStr = 'Jun';
      break;
    case 6:
      monthStr = 'Jul';
      break;
    case 7:
      monthStr = 'Aug';
      break;
    case 8:
      monthStr = 'Sep';
      break;
    case 9:
      monthStr = 'Oct';
      break;
    case 10:
      monthStr = 'Nov';
      break;
    case 11:
      monthStr = 'Dec';
      break;
  }

  return monthStr;
}

export function abbreviatedDateRange(start?: Date, end?: Date): string {
  /** Does not compare start and end date YEARS.
   * User of this function should make sure start Date
   * is prior to end Date
   */

  if (!start) return '';

  const [startMonth, startDay] = [
    numToShortMonth(start.getMonth()),
    start.getDate().toString(),
  ];

  if (!end) return `${startMonth} ${startDay}`;

  const [endMonth, endDay] = [
    numToShortMonth(end.getMonth()),
    end.getDate().toString(),
  ];

  if (startMonth === endMonth) {
    if (startDay === endDay) return `${startMonth} ${startDay}`;
    // else not same day
    return `${startMonth} ${startDay} - ${endDay}`;
  }

  // else different months
  return `${startMonth} ${startDay} - ${endMonth} ${endDay}`;
}

/** shorten a long string */
export function shorten(word: string, length: number) {
  const label = word.replace(/(<([^>]+)>)/gi, '');
  return label.length > length ? `${label.substring(0, length - 3)}...` : label;
}

export function getTourDisplayName(
  products: ConversationCheckAvailabilityProducts
): string {
  if (products.products.length > 1) {
    return `(${products.products.length}) tours selected`;
  } else if (products.products.length === 1) {
    return shorten(products.products[0].name, 30);
  } else {
    return '(select tour)';
  }
}

// Ensure no empty or invalid parameters necessary for checkAvailabilityV2
export function validateCheckAvailabilityParameters(
  checkAvailability: ConversationCheckAvailability
): { isValid: boolean; errorMessage: string } {
  const { dateTimeEnd, dateTimeStart, products, quantity } = checkAvailability;
  const returnObj = { isValid: true, errorMessage: '' };

  const datesAreValid = Boolean(dateTimeEnd) && dateTimeStart;
  const quantityIsValid = quantity > 0;
  const productsAreValid =
    products.products.length > 0 && products.products[0].productId;

  if (!datesAreValid) {
    returnObj.isValid = false;
    returnObj.errorMessage = 'Please select a valid date range';
  } else if (!quantityIsValid) {
    returnObj.isValid = false;
    returnObj.errorMessage = 'Please select number of participants';
  } else if (!productsAreValid) {
    returnObj.isValid = false;
    returnObj.errorMessage = 'Please select a tour';
  }

  return returnObj;
}

export function isInitialSearch(
  checkAvailability: ConversationCheckAvailability
): boolean {
  return checkAvailability.searches.length === 0;
}

export function createSearchMessage(
  products: CheckAvailabilityProduct[],
  dateTimeStart: Date,
  dateTimeEnd: Date,
  quantity: number
) {
  return `Tour: ${products
    .map(p => p.name)
    .join(', ')}, Dates: ${abbreviatedDateRange(
    dateTimeStart,
    dateTimeEnd
  )}, Quantity: ${quantity}`;
}

/** Parse Dialogflow messages, enabling support for
 * multiple Text Responses (separate chat bubbles) */
export const processDialogflowMessage = (
  message: Message
): {
  textMessages: string[];
  quickReplies: QuickRepliesMessage | undefined;
} => {
  if (!message) return { textMessages: [''], quickReplies: undefined };

  let textMessages: string[] = [];
  let quickReplies: QuickRepliesMessage;

  /** Skip processing for messages not sent by ChatBot */
  if (!isBot(message))
    return { textMessages: [message.text], quickReplies: undefined };

  if (isBot(message) && message.dialogflowQueryResult) {
    /** Only include PLATFORM_UNSPECIFIED messages for web client */
    const fulfillmentMessages = message.dialogflowQueryResult.fulfillmentMessages // @ts-expect-error
      .filter(mes => mes.platform === 'PLATFORM_UNSPECIFIED');

    if (fulfillmentMessages.length <= 1)
      return { textMessages: [message.text], quickReplies: undefined };

    /** `formattedMessages` field implemented May 28, 2021.
     * Older messages will need be detected by the for loop below */
    if (Array.isArray(message.formattedMessages))
      textMessages = message.formattedMessages;

    try {
      for (let i = 0; i < fulfillmentMessages.length; i++) {
        /** Will throw error if payload is not formatted correctly */
        if (fulfillmentMessages[i].message === 'payload') {
          const payloadData = (fulfillmentMessages[i] as CustomPayload).payload
            .fields.tomis.structValue.fields;
          const payloadType = payloadData.type.stringValue;

          if (payloadType === 'quickReplies') {
            quickReplies = payloadData as QuickRepliesMessage;
          }
          /** Add new payloadTypes here in the future (e.g. forms) */
        }

        if (
          fulfillmentMessages[i].message === 'text' &&
          !Array.isArray(message.formattedMessages)
        ) {
          // @ts-expect-error
          const text = fulfillmentMessages[i].text.text.toString();
          textMessages.push(text);
        }
      }
    } catch (err) {
      console.error('error displaying multiple textMessages: ', err);
      return { textMessages: [message.text], quickReplies: undefined };
    }
  } else {
    textMessages = [message.text];
  }
  return { textMessages, quickReplies };
};

const isBot = (message: Message): message is BotAgentMessage => {
  return message.type === MessageType.BotAgent;
};

/** Convert Firebase `timestamps` to JavaScript `Dates`.
 * Keep all other fields unchanged.
 * 
 * We frequently compare JS dates throughout the application, 
 * so we want to convert all Firebase Timestamps to JS Dates 
 * as soon as we retrieve them!
 * 
 * **Example usage**: 
 * ```typescript
 *  const allTokens = snapshot.docs.map(doc => {
      const data = transformFirebaseTimestampToJsDate<
        BrowserNotification
      >(doc.data());

      return {
        ref: doc.ref,
        value: data,
      };
    });
  ```
 *
 * https://firebase.google.com/docs/reference/js/v8/firebase.firestore.Timestamp */
export function transformFirebaseTimestampToJsDate<DataType>(
  data: Record<string, any>
) {
  for (const property in data) {
    if (data[property] instanceof firebase.firestore.Timestamp) {
      data[property] = data[property].toDate();
    }
  }

  return data as DataType;
}

/** Strip html entities and html tags from a string */
export function stripHtml(htmlString: string) {
  const decodedString = decode(htmlString);
  return decodedString.replace(/<[^>]*>/g, '');
}
